import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import Fuse from 'fuse.js';
import { exporteerExcel, exporteerPdf } from '../utils/exportUtils';

const PLUGIN_URL = typeof window !== 'undefined' && window.EUREKA_PLUGIN_URL
  ? window.EUREKA_PLUGIN_URL : '/';
const RESULTS_BASE_URL = `${PLUGIN_URL}uitslagen-output/results-`;
const RESULTS_YEARS = [2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];

const SERIE_CONFIG = {
  classic: { label: 'HRE de Klassieker', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('klassieker') },
  swimrun: { label: 'HRE de Swimrun', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('swimrun') && !(p.Onderdeel || '').toLowerCase().includes('onk') },
  onk:     { label: 'HRE het ONK', filter: null },
  kids:    { label: 'Het Kids Rondje', filter: (p) => (p.Onderdeel || '').toLowerCase().includes('kids') },
};
const SERIE_VOLGORDE = ['classic', 'swimrun', 'onk', 'kids'];
const ONK_CAT_VOLGORDE = ['Duo dames', 'Duo heren', 'Duo gemengd'];

// Verwerk zoekterm: '#A14' → exact startnummer match, anders substring op naam
export function parseZoekterm(zoekterm) {
  const trimmed = zoekterm.trim();
  if (trimmed.startsWith('#')) {
    return { type: 'startnr', waarde: trimmed.slice(1).toUpperCase() };
  }
  return { type: 'tekst', waarde: normalizeZoekTekst(trimmed) };
}

// Fuse.js instanties voor fuzzy zoeken
function maakFuseDeelnemers(deelnemers) {
  return new Fuse(deelnemers, {
    keys: [
      { name: '_zoekNaam', weight: 2 },
      { name: '_zoekGroepnaam', weight: 1.5 },
      { name: '_zoekWoonplaats', weight: 1 },
    ],
    threshold: 0.2,
    distance: 50,
    minMatchCharLength: 2,
    ignoreLocation: false,
  });
}

function maakFuseTeams(teams) {
  return new Fuse(teams, {
    keys: [
      { name: '_zoekTeamnaam', weight: 2 },
      { name: '_zoekDeelnemer1Naam', weight: 1.2 },
      { name: '_zoekDeelnemer2Naam', weight: 1.2 },
      { name: '_zoekDeelnemer1Woonplaats', weight: 0.8 },
      { name: '_zoekDeelnemer2Woonplaats', weight: 0.8 },
    ],
    threshold: 0.2,
    distance: 50,
    minMatchCharLength: 2,
    ignoreLocation: false,
  });
}

function matchDeelnemer(p, zoekterm, fuseInstance) {
  if (!zoekterm.trim()) return true;
  const { type, waarde } = parseZoekterm(zoekterm);
  if (type === 'startnr') {
    return (p.StartNr || '').toString().toUpperCase() === waarde;
  }
  // Fuzzy via Fuse.js — al gefilterd door fuseInstance
  return true; // filtering gebeurt via fuse.search()
}

export function zoekDeelnemers(deelnemers, zoekterm, fuseInstance) {
  if (!zoekterm.trim()) return deelnemers;
  const { type, waarde } = parseZoekterm(zoekterm);
  if (type === 'startnr') {
    // #22 → exacte match op startnummer
    return deelnemers.filter((p) => (p.StartNr || '').toString().toUpperCase() === waarde);
  }
  // Puur numeriek zonder # → substring match op startnummer
  if (/^\d+$/.test(waarde)) {
    return deelnemers.filter((p) => (p.StartNr || '').toString().includes(waarde));
  }
  return fuseInstance.search(waarde).map((r) => r.item);
}

function zoekTeams(teams, zoekterm, fuseInstance) {
  if (!zoekterm.trim()) return teams;
  const { type, waarde } = parseZoekterm(zoekterm);
  if (type === 'startnr') {
    return teams.filter((t) => {
      const d1 = t.deelnemer1 || {}; const d2 = t.deelnemer2 || {};
      return (d1.startNr || '').toString().toUpperCase() === waarde
        || (d2.startNr || '').toString().toUpperCase() === waarde;
    });
  }
  // Zoek ook op startnummer zonder # prefix als het puur numeriek is
  if (/^\d+$/.test(waarde)) {
    const nrMatches = teams.filter((t) => {
      const d1 = t.deelnemer1 || {}; const d2 = t.deelnemer2 || {};
      return (d1.startNr || '').toString().includes(waarde)
        || (d2.startNr || '').toString().includes(waarde);
    });
    if (nrMatches.length) return nrMatches;
  }
  return fuseInstance.search(waarde).map((r) => r.item);
}

import { normNaamCompact, normNaamFase1 } from '../utils/normNaam.js';
import InfoPopover from './InfoPopover';
import { landVlag, landNaam } from '../utils/landUtils.js';
import { useEilandersLookup } from '../hooks/useEilandersLookup.js';

export function normalizeName(name) {
  return normNaamCompact(name);
}
export function normalizeZoekTekst(tekst) {
  return normNaamFase1(tekst || '').trim();
}

function verrijkDeelnemerVoorZoeken(p) {
  return {
    ...p,
    _zoekNaam: normalizeZoekTekst(p.Naam),
    _zoekGroepnaam: normalizeZoekTekst(p.Groepnaam),
    _zoekWoonplaats: normalizeZoekTekst(p.Woonplaats),
  };
}

function verrijkTeamVoorZoeken(team) {
  const d1 = team.deelnemer1 || {};
  const d2 = team.deelnemer2 || {};
  return {
    ...team,
    _zoekTeamnaam: normalizeZoekTekst(team.teamnaam),
    _zoekDeelnemer1Naam: normalizeZoekTekst(d1.naam),
    _zoekDeelnemer2Naam: normalizeZoekTekst(d2.naam),
    _zoekDeelnemer1Woonplaats: normalizeZoekTekst(d1.woonplaats),
    _zoekDeelnemer2Woonplaats: normalizeZoekTekst(d2.woonplaats),
  };
}
export function getNameVariants(p) {
  const voornaam = (p.Voornaam || '').trim();
  const tv = (p.Tussenvoegsel || '').trim();
  const achternaam = (p.Achternaam || '').trim();
  const naam = (p.Naam || '').trim();
  const variants = [];
  if (voornaam && achternaam) {
    variants.push(`${voornaam}${tv ? ` ${tv}` : ''} ${achternaam}`.trim());
    variants.push(`${achternaam}, ${voornaam}${tv ? ` ${tv}` : ''}`.trim());
  }
  if (naam) variants.push(naam);
  return variants.filter(Boolean);
}

// Pure functie voor naam-matching — testbaar zonder fetch
export function findResultsForParticipant(participant, resultsByYear) {
  const variants = getNameVariants(participant).map(normalizeName);
  const found = [];
  Object.entries(resultsByYear).forEach(([year, data]) => {
    const rows = data.uitslagen || (Array.isArray(data) ? data : []);
    rows.filter((r) => variants.includes(normalizeName(r.naam || r.Naam || ''))).forEach((r) => {
      found.push({ year: Number(year), ...r });
    });
  });
  return found;
}

async function fetchJson(url) {
  let res;
  try { res = await fetch(url); } catch (e) {
    throw new Error(`Kan ${url} niet bereiken.`);
  }
  if (res.status === 404) throw new Error(`Bestand niet gevonden: ${url}`);
  if (!res.ok) throw new Error(`Fout bij laden ${url}: HTTP ${res.status}`);
  try { return await res.json(); } catch (e) {
    throw new Error(`${url} is geen geldig JSON bestand.`);
  }
}

function useFetchQueue(keys, fetchFn, maxConcurrent = 10) {
  const [results, setResults] = useState({});
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const resultsRef = useRef({});
  useEffect(() => {
    setResults({});
    resultsRef.current = {};
    queueRef.current = keys.filter(Boolean);
    activeRef.current = 0;
    let cancelled = false;
    const next = () => {
      if (!queueRef.current.length || activeRef.current >= maxConcurrent) return;
      const key = queueRef.current.shift();
      if (!key || resultsRef.current[key]) { next(); return; }
      activeRef.current += 1;
      fetchFn(key)
        .then((r) => { resultsRef.current[key] = r; setResults((s) => ({ ...s, [key]: r })); })
        .catch(() => { resultsRef.current[key] = []; setResults((s) => ({ ...s, [key]: [] })); })
        .finally(() => { activeRef.current -= 1; if (!cancelled) next(); });
      if (activeRef.current < maxConcurrent) next();
    };
    for (let i = 0; i < Math.min(maxConcurrent, queueRef.current.length); i += 1) next();
    return () => { cancelled = true; };
  }, [keys, fetchFn, maxConcurrent]);
  return results;
}

async function fetchOldResultsForParticipant(participant) {
  const variants = getNameVariants(participant).map(normalizeName);
  const found = [];
  let cache;
  if (typeof window !== 'undefined' && 'caches' in window) cache = await caches.open('hre-uitslagen-cache-v1');
  await Promise.all(RESULTS_YEARS.map(async (year) => {
    const url = `${RESULTS_BASE_URL}${year}.json`;
    let data = null;
    if (cache) { try { const c = await cache.match(url); if (c) data = await c.json(); } catch (_) { /* ignore */ } }
    if (!data) {
      try { const r = await fetch(url); if (r.ok) { data = await r.clone().json(); if (cache) await cache.put(url, r); } } catch (_) { /* ignore */ }
    }
    if (data) {
      (data.uitslagen || (Array.isArray(data) ? data : [])).filter((r) => variants.includes(normalizeName(r.naam || r.Naam || ''))).forEach((r) => found.push({ year, ...r }));
    }
  }));
  return found;
}

function downloadCsv(filename, rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = `\uFEFF${[headers.map(esc).join(';'), ...rows.map((r) => headers.map((h) => esc(r[h])).join(';'))].join('\n')}`;
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })), download: filename });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

const S = {
  th: { padding: '0.4em 0.6em', borderBottom: '2px solid #b4c002', fontWeight: 'bold', whiteSpace: 'nowrap' },
  td: { padding: '0.4em 0.6em', borderBottom: '1px solid #e8e8e8', verticalAlign: 'top' },
  opm: { fontSize: '0.85em', color: '#555', fontStyle: 'italic' },
  fout: { background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '1em 1.2em', color: '#7a4f00', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9em' },
  badge: { display: 'inline-block', background: '#b4c002', color: '#222', borderRadius: 10, padding: '0.1em 0.5em', fontSize: '0.8em', fontWeight: 'bold', marginLeft: '0.3em' },
};

function Foutmelding({ fout }) {
  return (
    <div style={S.fout}>
      <strong>⚠ Startlijst kon niet worden geladen</strong>
      <br /><br />
      {fout}
      <br /><br />
      <em>Tip: upload het JSON bestand via WordPress → Media → Nieuw bestand toevoegen naar wp-content/uploads/</em>
    </div>
  );
}

function InfoPaneel({ fout }) {
  return (
    <div style={{ marginBottom: '1em' }}>
      {fout && <Foutmelding fout={fout} />}
      <details style={{ fontSize: '0.88em', color: '#555', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.4em 0.8em', background: '#fafafa' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#001a4d', userSelect: 'none' }}>
          ℹ Toelichting &amp; uitleg kolommen
        </summary>
        <div style={{ marginTop: '0.6em', lineHeight: 1.6 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '0.8em' }}>
            <thead>
              <tr style={{ background: '#f0f4ff' }}>
                <th style={{ padding: '0.3em 0.6em', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Kolom</th>
                <th style={{ padding: '0.3em 0.6em', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Uitleg</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.3em 0.6em', fontWeight: 'bold' }}>Startnr</td>
                <td style={{ padding: '0.3em 0.6em' }}><strong>TBD</strong> = To Be Determined — definitief startnummer volgt later</td>
              </tr>
              <tr style={{ background: '#f8f8f8' }}>
                <td style={{ padding: '0.3em 0.6em', fontWeight: 'bold' }}>AN</td>
                <td style={{ padding: '0.3em 0.6em' }}>Aanmeldnummer — het nummer dat bij inschrijving is toegewezen</td>
              </tr>
              <tr>
                <td style={{ padding: '0.3em 0.6em', fontWeight: 'bold' }}>Uitslagen HRE</td>
                <td style={{ padding: '0.3em 0.6em' }}>Eerdere deelnames aan Het Rondje Eilanden (2012–2026)</td>
              </tr>
              <tr style={{ background: '#f8f8f8' }}>
                <td style={{ padding: '0.3em 0.6em', fontWeight: 'bold' }}>#A14</td>
                <td style={{ padding: '0.3em 0.6em' }}>Zoek exact op aanmeldnummer door een <code>#</code> voor het nummer te zetten</td>
              </tr>
            </tbody>
          </table>
          <details style={{ marginTop: '0.4em' }}>
            <summary style={{ cursor: 'pointer', color: '#666', userSelect: 'none' }}>Dankwoord open source</summary>
            <p style={{ marginTop: '0.5em', color: '#777' }}>
              EUREKA is gebouwd op de schouders van reuzen. Met dank aan:{' '}
              <a href="https://www.fusejs.io/" target="_blank" rel="noreferrer">Fuse.js</a> (fuzzy zoeken),{' '}
              <a href="https://exceljs.github.io/exceljs/" target="_blank" rel="noreferrer">ExcelJS</a> (Excel export),{' '}
              <a href="https://jspdf.com/" target="_blank" rel="noreferrer">jsPDF</a> +{' '}
              <a href="https://www.npmjs.com/package/jspdf-autotable" target="_blank" rel="noreferrer">AutoTable</a> (PDF export),{' '}
              <a href="https://react.dev/" target="_blank" rel="noreferrer">React</a> en{' '}
              <a href="https://vitejs.dev/" target="_blank" rel="noreferrer">Vite</a>.
            </p>
          </details>
        </div>
      </details>
    </div>
  );
}

function GeldigVanaf({ datum }) {
  if (!datum) return null;
  return (
    <div style={{
      background: '#fff8e1',
      border: '1px solid #ffc107',
      borderRadius: 6,
      padding: '0.6em 1em',
      marginBottom: '1em',
      fontSize: '0.9em',
      color: '#5a4000',
    }}>
      ℹ️ Voorlopige startlijst — bijgewerkt op <strong>{datum}</strong>. Wijzigingen voorbehouden.
    </div>
  );
}

function SerieCheckboxes({ zichtbareSeries, actieveSeries, onChange, versie }) {
  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4em' }}>
        <div style={{ fontSize: '0.82em', color: '#555', fontStyle: 'italic' }}>
          Selecteer de in zoekresultaten op te nemen startseries:
        </div>
        {versie && <span style={{ fontSize: '0.75em', color: '#aaa' }}>v{versie}</span>}
      </div>
      <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap' }}>
        {zichtbareSeries.map((s) => (
          <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.4em', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={actieveSeries.includes(s)}
              onChange={() => onChange(s)}
              style={{ accentColor: '#001a4d', width: '1.1em', height: '1.1em' }}
            />
            <span style={{ fontSize: '0.95em', color: '#001a4d', fontWeight: actieveSeries.includes(s) ? 'bold' : 'normal' }}>
              {SERIE_CONFIG[s]?.label || s}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ZoekBalk({ value, onChange, placeholder, speaker }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '1em' }}>
      <label htmlFor="startlijst-zoek" style={{ fontWeight: 'bold', color: '#001a4d', whiteSpace: 'nowrap', fontSize: '0.95em' }}>Zoek</label>
      <div style={{ position: 'relative', flex: 1 }}>
        <input
          id="startlijst-zoek"
          type="text"
          placeholder={placeholder || 'Zoeken...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%', padding: '0.6em 2.5em 0.6em 0.8em', fontSize: '1em', border: '1.5px solid #b4c002', borderRadius: 6, boxSizing: 'border-box' }}
        />
        {value && (
          <button type="button" onClick={() => onChange('')} style={{ position: 'absolute', right: '0.5em', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1.3em' }}>×</button>
        )}
      </div>
      <InfoPopover>
        <strong>Zoeken</strong><br />
        Zoekfunctionaliteit probeert te corrigeren voor kleine typefouten, zoals 'Linda' i.p.v. 'Linde'.<br /><br />
        Prefix <code>#</code> voor exact startnummer:<br />
        <code>#A14</code> → alleen startnummer A14<br /><br />
        {speaker ? 'In speaker modus kun je ook zoeken op woonplaats.' : 'Zoeken op woonplaats is niet beschikbaar in de publieke weergave.'}<br /><br />
        <em>Fuzzy zoeken via Fuse.js (open source).</em>
      </InfoPopover>
    </div>
  );
}

function ONKWeergave({ teams, loading, fout, speaker, zoekterm, onGefilterdCount, oldResults, notes = {} }) {
  const teamsVoorZoeken = useMemo(
    () => teams.map(verrijkTeamVoorZoeken),
    [teams],
  );
  const fuseTeams = useMemo(() => maakFuseTeams(teamsVoorZoeken), [teamsVoorZoeken]);

  const gefilterd = useMemo(() => {
    if (!zoekterm.trim()) return teamsVoorZoeken;
    return zoekTeams(teamsVoorZoeken, zoekterm, fuseTeams);
  }, [teamsVoorZoeken, zoekterm, fuseTeams]);

  // Rapporteer aantal hits naar parent
  useEffect(() => {
    if (onGefilterdCount) onGefilterdCount(zoekterm.trim() ? gefilterd.length : 0);
  }, [gefilterd, zoekterm, onGefilterdCount]);

  const perCat = useMemo(() => {
    const map = Object.fromEntries(ONK_CAT_VOLGORDE.map((c) => [c, []]));
    gefilterd.forEach((t) => { const c = t.categorie || 'Overig'; if (!map[c]) map[c] = []; map[c].push(t); });
    return map;
  }, [gefilterd]);

  if (loading) return <div style={{ padding: '1em', color: '#666' }}>Laden...</div>;
  if (fout) return (
    <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '1em 1.2em', color: '#7a4f00', fontSize: '0.9em' }}>
      <strong>⚠ ONK teams konden niet worden geladen</strong><br /><br />
      {fout}
    </div>
  );

  // Bij actieve zoekterm zonder hits: niets tonen (geen leeg blok)
  if (zoekterm.trim() && gefilterd.length === 0) return null;

  return (
    <div>
      <p style={{ color: '#555', fontSize: '0.95em', marginBottom: '1em' }}>
        {teams.length} teams · {teams.reduce((a, t) => a + (t.deelnemer1?.naam ? 1 : 0) + (t.deelnemer2?.naam ? 1 : 0), 0)} deelnemers
      </p>
      {ONK_CAT_VOLGORDE.map((cat) => {
        const lijst = perCat[cat] || [];
        if (!lijst.length) return null;
        return (
          <section key={cat} style={{ marginBottom: '2em' }}>
            <h3 style={{ background: '#001a4d', color: '#fff', padding: '0.4em 0.8em', borderRadius: 4, fontSize: '1em', marginBottom: '0.5em' }}>
              {cat} ({lijst.length} teams)
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' }}>
              <thead>
                <tr style={{ background: '#f0f4ff', textAlign: 'left' }}>
                  <th style={S.th}>Startnr</th>
                  <th style={S.th}>Starttijd</th>
                  <th style={S.th}>Teamnaam</th>
                  <th style={S.th}>Deelnemer 1</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Land</th>
                  {speaker && <th style={S.th}>Woonplaats</th>}
                  <th style={S.th}>Uitslagen HRE D1</th>
                  <th style={S.th}>Deelnemer 2</th>
                  <th style={{ ...S.th, textAlign: 'center' }}>Land</th>
                  {speaker && <th style={S.th}>Woonplaats</th>}
                  <th style={S.th}>Uitslagen HRE D2</th>
                  {speaker && <th style={S.th}>Notitie</th>}
                </tr>
              </thead>
              <tbody>
                {lijst.map((team, i) => {
                  const d1 = team.deelnemer1 || {}; const d2 = team.deelnemer2 || {};
                  const r1 = oldResults?.[d1.naam] || [];
                  const r2 = oldResults?.[d2.naam] || [];
                  const uitslagenTekst = (results) => results.length === 0 ? '—' : results.map((r) => `${r.year}: ${r.totaaltijd || '—'}${r.plaats && r.plaats !== '.' ? ` (${r.plaats}e)` : ''}`).join(', ');
                  return (
                    <tr key={`${team.teamnaam}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
                      <td style={{ ...S.td, fontWeight: 'bold' }}>{d1.startNr || '—'}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace' }}>{team.starttijd || '—'}</td>
                      <td style={{ ...S.td, fontWeight: 'bold' }}>{team.teamnaam || '—'}</td>
                      <td style={S.td}>{d1.naam || '—'}</td>
                      <td style={{ ...S.td, textAlign: 'center' }} title={landNaam(d1.landcode)}>
                        <span style={{ fontSize: '1.3em' }}>{landVlag(d1.landcode) || d1.landcode || '—'}</span>
                      </td>
                      {speaker && <td style={S.td}>{d1.woonplaats || '—'}</td>}
                      <td style={{ ...S.td, fontSize: '0.82em', color: '#555' }}>{uitslagenTekst(r1)}</td>
                      <td style={S.td}>{d2.naam || '—'}</td>
                      <td style={{ ...S.td, textAlign: 'center' }} title={landNaam(d1.landcode)}>
                        <span style={{ fontSize: '1.3em' }}>{landVlag(d1.landcode) || d1.landcode || '—'}</span>
                      </td>
                      {speaker && <td style={S.td}>{d2.woonplaats || '—'}</td>}
                      <td style={{ ...S.td, fontSize: '0.82em', color: '#555' }}>{uitslagenTekst(r2)}</td>
                      {speaker && (() => {
                        const n1 = notes[d1.naam];
                        const n2 = notes[d2.naam];
                        const renderNote = (n) => n ? (
                          n.url
                            ? <a href={n.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.82em', color: '#001a4d', display: 'block' }}>{n.tekst || n.url}</a>
                            : <span style={{ fontSize: '0.82em', display: 'block' }}>{n.tekst}</span>
                        ) : null;
                        return (
                          <td style={{ ...S.td, ...S.opm }}>
                            {renderNote(n1)}
                            {renderNote(n2)}
                            {!n1 && !n2 && (team.opmerking || '')}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}

// Normaliseert een ONK team naar een rij-object compatibel met DeelnemersWeergave
export function normaliseerTeam(team) {
  const d1 = team.deelnemer1 || {};
  const d2 = team.deelnemer2 || {};
  // Categorie mapping: "Duo heren" → "Heren", "Duo dames" → "Dames", "Duo gemengd" → "Gemengd"
  const cat = (team.categorie || '').toLowerCase();
  let Categorie = 'Gemengd';
  if (cat.includes('heren')) Categorie = 'Heren';
  else if (cat.includes('dames')) Categorie = 'Dames';

  return {
    _type: 'team',
    StartNr: d1.startNr || '—',
    AN: d1.AN || '',
    Naam: team.teamnaam || '—',
    Starttijd: team.starttijd || '',
    Onderdeel: 'HRE het ONK (3 rondjes - duowedstrijd)',
    Categorie,
    Landcode: d1.landcode || '',
    Woonplaats: d1.woonplaats || '',
    // ONK-specifieke velden
    _deelnemer1: d1,
    _deelnemer2: d2,
    _opmerking: team.opmerking || '',
  };
}

// Korte weergavelabel voor de Serie-kolom
function serieKort(onderdeel) {
  const o = (onderdeel || '').toLowerCase();
  if (o.includes('kids')) return 'Kids';
  if (o.includes('onk')) return 'ONK';
  if (o.includes('swimrun') || o.includes('2 rondjes')) return 'Swimrun';
  return 'Klassieker';
}

// Uitslagen cel: toon max 3 recente jaren, rest inklapbaar via <details>
function UitslagenCel({ results }) {
  if (!results || results.length === 0) return <span style={{ color: '#bbb', fontSize: '0.85em' }}>—</span>;

  const rij = (r) => (
    <li key={`${r.year}-${r.totaaltijd || r.tijdrit}`} style={{ fontSize: '0.9em', listStyle: 'none', padding: 0, margin: 0 }}>
      {r.year}: {r.totaaltijd || r.tijdrit || '—'}
      {r.plaats && r.plaats !== '.' ? `, ${r.plaats}e` : ''}
    </li>
  );

  // Sorteer recenter eerst
  const gesorteerd = [...results].sort((a, b) => b.year - a.year);
  const recent = gesorteerd.slice(0, 3);
  const ouder = gesorteerd.slice(3);

  if (ouder.length === 0) {
    return <ul style={{ margin: 0, padding: 0 }}>{recent.map(rij)}</ul>;
  }

  return (
    <details>
      <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#555', userSelect: 'none', listStyle: 'none' }}>
        {gesorteerd.length}× HRE
      </summary>
      <ul style={{ margin: '0.2em 0 0', padding: 0 }}>
        {recent.map(rij)}
        <li style={{ fontSize: '0.9em', listStyle: 'none', padding: 0, margin: 0, color: '#aaa', fontStyle: 'italic' }}>— ouder —</li>
        {ouder.map(rij)}
      </ul>
    </details>
  );
}

function serieVanDeelnemer(p) {
  const o = (p.Onderdeel || '').toLowerCase();
  if (o.includes('kids')) return 'kids';
  if (o.includes('onk') || o.includes('3 rondjes')) return 'onk';
  if (o.includes('swimrun') || o.includes('2 rondjes')) return 'swimrun';
  return 'classic';
}

function DeelnemersWeergave({ deelnemers, loading, fout, speaker, oldResults, allFetched, onExport, onExportExcel, onExportPdf, zoekterm, actieveSeries, onkAantal, notes = {} }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [landFilter, setLandFilter] = useState('alle');
  const [catFilter, setCatFilter] = useState('alle');

  // Beschikbare categorieën in de huidige lijst
  const beschikbareCategorieen = useMemo(() => {
    const set = new Set(deelnemers.map((p) => p.Categorie).filter(Boolean));
    return [...set].sort();
  }, [deelnemers]);

  const heeftGemengd = beschikbareCategorieen.includes('Gemengd');

  // Unieke landen in de huidige deelnemerslijst
  const beschikbareLanden = useMemo(() => {
    const set = new Set(deelnemers.map((p) => p.Landcode).filter(Boolean));
    return [...set].sort();
  }, [deelnemers]);

  function handleSort(col) {
    if (sortCol === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortCol(null); setSortDir('asc'); } // derde klik: reset
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function sortIndicator(col) {
    if (sortCol !== col) return <span style={{ color: '#ccc', fontSize: '0.8em', marginLeft: '0.3em' }}>⇅</span>;
    return <span style={{ color: '#001a4d', fontSize: '0.8em', marginLeft: '0.3em' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  const gesorteerd = useMemo(() => {
    const basis = [...deelnemers].sort((a, b) => {
      const sA = SERIE_SORT_ORDER[serieVanDeelnemer(a)] ?? 9;
      const sB = SERIE_SORT_ORDER[serieVanDeelnemer(b)] ?? 9;
      if (sA !== sB) return sA - sB;
      // Binnen alle series op starttijd (classic rolling, rest mass start per geslacht)
      const tA = (a.Starttijd || '').replace(':', '');
      const tB = (b.Starttijd || '').replace(':', '');
      if (tA && tB && tA !== tB) return tA.localeCompare(tB);
      const nA = parseInt(a.StartNr, 10) || 9999;
      const nB = parseInt(b.StartNr, 10) || 9999;
      return nA - nB;
    });

    if (!sortCol) return basis;

    return [...basis].sort((a, b) => {
      let vA, vB;
      if (sortCol === 'naam') {
        vA = (a.Naam || '').trim().split(/\s+/).pop().toLowerCase();
        vB = (b.Naam || '').trim().split(/\s+/).pop().toLowerCase();
        return sortDir === 'asc' ? vA.localeCompare(vB, 'nl') : vB.localeCompare(vA, 'nl');
      }
      if (sortCol === 'startnr') {
        vA = parseInt(a.StartNr, 10) || 9999;
        vB = parseInt(b.StartNr, 10) || 9999;
        return sortDir === 'asc' ? vA - vB : vB - vA;
      }
      if (sortCol === 'starttijd') {
        vA = (a.Starttijd || '').replace(':', '');
        vB = (b.Starttijd || '').replace(':', '');
        return sortDir === 'asc' ? vA.localeCompare(vB) : vB.localeCompare(vA);
      }
      return 0;
    });
  }, [deelnemers, sortCol, sortDir]);

  const deelnemersVoorZoeken = useMemo(
    () => gesorteerd.map(verrijkDeelnemerVoorZoeken),
    [gesorteerd],
  );

  const fuseDeelnemers = useMemo(
    () => maakFuseDeelnemers(deelnemersVoorZoeken),
    [deelnemersVoorZoeken],
  );

  const gefilterd = useMemo(() => {
    const zoekResultaat = zoekDeelnemers(deelnemersVoorZoeken, zoekterm, fuseDeelnemers);
    let resultaat = zoekResultaat;
    if (landFilter !== 'alle') {
      resultaat = resultaat.filter((p) => (p.Landcode || '') === landFilter);
    }
    if (catFilter !== 'alle') {
      resultaat = resultaat.filter((p) => {
        const cat = (p.Categorie || '').toLowerCase();
        if (catFilter === 'heren') return cat === 'heren';
        if (catFilter === 'dames') return cat === 'dames';
        if (catFilter === 'gemengd') return cat === 'gemengd';
        return true;
      });
    }
    return resultaat;
  }, [deelnemersVoorZoeken, zoekterm, fuseDeelnemers, landFilter, catFilter]);

  if (loading) return <div style={{ padding: '1em', color: '#666' }}>Laden...</div>;
  if (fout) return <Foutmelding fout={fout} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em', flexWrap: 'wrap', gap: '0.5em' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
          <p style={{ color: '#555', fontSize: '0.95em', margin: 0 }}>{gefilterd.length} deelnemers{zoekterm ? ` (gefilterd uit ${deelnemers.length + (onkAantal || 0)})` : ''}</p>
          {(beschikbareCategorieen.length > 1 || heeftGemengd) && (
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              style={{ fontSize: '0.9em', padding: '0.2em 0.5em', border: '1px solid #b4c002', borderRadius: 4 }}
              aria-label="Filter op categorie"
            >
              <option value="alle">Alle categorieën</option>
              <option value="heren">Heren</option>
              <option value="dames">Dames</option>
              {heeftGemengd && <option value="gemengd">Gemengd (ONK)</option>}
            </select>
          )}
          {beschikbareLanden.length > 1 && (
            <select
              value={landFilter}
              onChange={(e) => setLandFilter(e.target.value)}
              style={{ fontSize: '0.9em', padding: '0.2em 0.5em', border: '1px solid #b4c002', borderRadius: 4 }}
              aria-label="Filter op land"
            >
              <option value="alle">Alle landen</option>
              {beschikbareLanden.map((lc) => (
                <option key={lc} value={lc}>{landVlag(lc)} {lc}</option>
              ))}
            </select>
          )}
        </div>
        {speaker && (
          <div style={{ display: 'flex', gap: '0.5em' }}>
            <button
              type="button"
              onClick={onExport}
              disabled={!allFetched}
              style={{ padding: '0.4em 1em', fontWeight: 'bold', background: allFetched ? '#b4c002' : '#eee', color: allFetched ? '#222' : '#888', border: '1.5px solid #b4c002', borderRadius: 6, cursor: allFetched ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
              title={allFetched ? 'Exporteer naar CSV' : 'Wacht tot alle uitslagen zijn geladen'}
            >
              {allFetched ? 'CSV' : 'Laden...'}
            </button>
            <button
              type="button"
              onClick={onExportExcel}
              disabled={!allFetched}
              style={{ padding: '0.4em 1em', fontWeight: 'bold', background: allFetched ? '#217346' : '#eee', color: allFetched ? '#fff' : '#888', border: '1.5px solid #217346', borderRadius: 6, cursor: allFetched ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
              title={allFetched ? 'Exporteer naar Excel' : 'Wacht tot alle uitslagen zijn geladen'}
            >
              {allFetched ? 'Excel' : 'Laden...'}
            </button>
            <button
              type="button"
              onClick={onExportPdf}
              disabled={!allFetched}
              style={{ padding: '0.4em 1em', fontWeight: 'bold', background: allFetched ? '#c0392b' : '#eee', color: allFetched ? '#fff' : '#888', border: '1.5px solid #c0392b', borderRadius: 6, cursor: allFetched ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}
              title={allFetched ? 'Exporteer naar PDF' : 'Wacht tot alle uitslagen zijn geladen'}
            >
              {allFetched ? 'PDF' : 'Laden...'}
            </button>
          </div>
        )}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' }}>
        <thead>
          <tr style={{ background: '#f0f4ff', textAlign: 'left' }}>
            <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startnr')}>Startnr {sortIndicator('startnr')}</th>
            <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('starttijd')}>Starttijd {sortIndicator('starttijd')}</th>
            <th style={{ ...S.th, color: '#999', fontSize: '0.85em' }}>AN <InfoPopover>Aanmeldnummer — het nummer dat bij inschrijving is toegewezen. Gebruik <code>#A14</code> om exact op dit nummer te zoeken.</InfoPopover></th>
            <th style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('naam')}>Naam {sortIndicator('naam')}</th>
            {speaker && <th style={S.th}>Groepnaam</th>}
            {speaker && <th style={S.th}>Woonplaats</th>}
            <th style={{ ...S.th, textAlign: 'center' }}>Land</th>
            <th style={S.th}>Serie</th>
            <th style={S.th}>Categorie</th>
            <th style={S.th}>Uitslagen HRE</th>
            {speaker && <th style={S.th}>Notitie</th>}
          </tr>
        </thead>
        <tbody>
          {gefilterd.length === 0 && (zoekterm || catFilter !== 'alle' || landFilter !== 'alle') && (
            <tr>
              <td colSpan={99} style={{ padding: '1.5em', textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                Geen resultaten voor huidige filter.
                {catFilter === 'gemengd' && !heeftGemengd ? ' Zet "HRE het ONK" aan om gemengde teams te zien.' : ''}
              </td>
            </tr>
          )}
          {gefilterd.map((p, i) => {
            if (p._type === 'team') {
              // ONK team rij — compact: teamnaam + beide deelnemers
              const d1 = p._deelnemer1 || {};
              const d2 = p._deelnemer2 || {};
              const r1 = oldResults?.[d1.naam] || [];
              const r2 = oldResults?.[d2.naam] || [];
              const note1 = notes[d1.naam];
              const note2 = notes[d2.naam];
              return (
                <tr key={`team-${p.StartNr}-${i}`} style={{ background: i % 2 === 0 ? '#fff0fa' : '#ffe8f5', borderLeft: '3px solid #b4c002' }}>
                  <td style={{ ...S.td, fontWeight: 'bold' }}>{p.StartNr || '—'}</td>
                  <td style={{ ...S.td, fontFamily: 'monospace' }}>{p.Starttijd || '—'}</td>
                  <td style={{ ...S.td, color: '#bbb', fontSize: '0.8em' }}>{d1.AN || ''}</td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.2em' }}>{p.Naam}</div>
                    <div style={{ fontSize: '0.88em', color: '#555' }}>
                      {d1.naam || '—'}
                      {speaker && d1.woonplaats ? ` · ${d1.woonplaats}` : ''}
                    </div>
                    <div style={{ fontSize: '0.88em', color: '#555' }}>
                      {d2.naam || '—'}
                      {speaker && d2.woonplaats ? ` · ${d2.woonplaats}` : ''}
                    </div>
                  </td>
                  {speaker && <td style={{ ...S.td, fontSize: '0.85em', color: '#555' }}>{p._opmerking || '—'}</td>}
                  {speaker && <td style={S.td}>{d1.woonplaats || '—'}</td>}
                  <td style={{ ...S.td, textAlign: 'center' }} title={landNaam(d1.landcode)}>
                    <span style={{ fontSize: '1.3em' }}>{landVlag(d1.landcode) || d1.landcode || '—'}</span>
                  </td>
                  <td style={{ ...S.td, fontSize: '0.85em', color: '#555' }}>ONK</td>
                  <td style={S.td}>{p.Categorie}</td>
                  <td style={S.td}>
                    <UitslagenCel results={r1} />
                    {r2.length > 0 && <div style={{ marginTop: '0.3em', borderTop: '1px solid #eee', paddingTop: '0.3em' }}><UitslagenCel results={r2} /></div>}
                  </td>
                  {speaker && (
                    <td style={{ ...S.td, ...S.opm }}>
                      {note1 && (note1.url ? <a href={note1.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.85em', color: '#001a4d', display: 'block' }}>{note1.tekst || note1.url}</a> : <span style={{ fontSize: '0.85em', display: 'block' }}>{note1.tekst}</span>)}
                      {note2 && (note2.url ? <a href={note2.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.85em', color: '#001a4d', display: 'block' }}>{note2.tekst || note2.url}</a> : <span style={{ fontSize: '0.85em', display: 'block' }}>{note2.tekst}</span>)}
                      {!note1 && !note2 && (p._opmerking || '')}
                    </td>
                  )}
                </tr>
              );
            }

            // Individuele deelnemer rij
            const results = oldResults?.[p.Naam] || [];
            return (
              <tr key={p.StartNr || i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
                <td style={{ ...S.td, fontWeight: 'bold' }}>{p.StartNr || '—'}</td>
                <td style={{ ...S.td, fontFamily: 'monospace' }}>{p.Starttijd || '—'}</td>
                <td style={{ ...S.td, color: '#bbb', fontSize: '0.8em' }}>{p.AN}</td>
                <td style={S.td}>{p.Naam}</td>
                {speaker && <td style={{ ...S.td, fontSize: '0.85em', color: '#555' }}>{p.Groepnaam || '—'}</td>}
                {speaker && <td style={S.td}>{p.Woonplaats}</td>}
                <td style={{ ...S.td, textAlign: 'center' }} title={landNaam(p.Landcode)}>
                  {p.Landcode ? (
                    <span style={{ fontSize: '1.3em', lineHeight: 1 }} title={landNaam(p.Landcode)}>
                      {landVlag(p.Landcode) || p.Landcode}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ ...S.td, fontSize: '0.85em', color: '#555' }}>{serieKort(p.Onderdeel)}</td>
                <td style={S.td}>{p.Categorie}</td>
                <td style={S.td}>
                  {results.length > 0 ? (
                    <UitslagenCel results={results} />
                  ) : oldResults?.[p.Naam] !== undefined ? (
                    <span style={{ color: '#bbb', fontSize: '0.85em' }}>—</span>
                  ) : (
                    <span style={{ color: '#bbb', fontSize: '0.85em' }}>laden...</span>
                  )}
                </td>
                {speaker && (() => {
                  const note = notes[p.Naam];
                  return (
                    <td style={{ ...S.td, ...S.opm }}>
                      {note ? (
                        note.url ? (
                          <a href={note.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.85em', color: '#001a4d' }}>
                            {note.tekst || note.url}
                          </a>
                        ) : (
                          <span style={{ fontSize: '0.85em' }}>{note.tekst}</span>
                        )
                      ) : ''}
                    </td>
                  );
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Startlijst({ serie = 'all', speaker = false, jaar = 2026 }) {
  const enkeleZichtbareSeries = serie === 'all' ? SERIE_VOLGORDE : [serie];
  const individueleSeriesDefault = enkeleZichtbareSeries.filter((s) => s !== 'onk');
  // ONK standaard aan bij 'all', of alleen ONK bij serie='onk'
  const [actieveSeries, setActieveSeries] = useState(
    serie === 'onk' ? ['onk'] : [...individueleSeriesDefault, 'onk'],
  );
  const [zoekterm, setZoekterm] = useState('');
  const [deelnemers, setDeelnemers] = useState([]);
  const [geldigVanaf, setGeldigVanaf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fout, setFout] = useState(null);
  const [onkTeams, setOnkTeams] = useState([]);
  const [onkLoading, setOnkLoading] = useState(true);
  const [onkFout, setOnkFout] = useState(null);
  const handleGeldigVanaf = useCallback((d) => setGeldigVanaf((prev) => prev || d), []);

  const heeftOnk = actieveSeries.includes('onk');
  const heeftIndividueel = actieveSeries.some((s) => s !== 'onk');

  // ONK data ophalen in de parent
  useEffect(() => {
    if (!heeftOnk) return;
    setOnkLoading(true); setOnkFout(null);
    fetchJson(`${PLUGIN_URL}onk-teams-${jaar}.json`)
      .then((data) => {
        const geladen = data.teams || [];
        setOnkTeams(geladen);
        if (data.geldigVanaf) handleGeldigVanaf(data.geldigVanaf);
        setOnkLoading(false);
      })
      .catch((e) => { setOnkFout(e.message); setOnkLoading(false); });
  }, [jaar, heeftOnk, handleGeldigVanaf]);

  // Laad speaker notes (alleen in speaker modus)
  const [notes, setNotes] = useState({});
  useEffect(() => {
    if (!speaker) return;
    fetchJson(`${PLUGIN_URL}notes-${jaar}.json`)
      .then((data) => setNotes(data || {}))
      .catch(() => setNotes({}));
  }, [jaar, speaker]);

  // Laad participants JSON als er een individuele serie actief is
  const bestandsnaam = speaker ? `participants-${jaar}-speaker.json` : `participants-${jaar}.json`;
  const participantsUrl = `${PLUGIN_URL}startlijst-output/${bestandsnaam}`;
  useEffect(() => {
    if (!heeftIndividueel) return;
    setLoading(true); setFout(null);
    Promise.all([
      fetchJson(participantsUrl),
      fetchJson(`${PLUGIN_URL}meta-${jaar}.json`).catch(() => null),
    ]).then(([data, meta]) => {
      setDeelnemers(Array.isArray(data) ? data : (data.deelnemers || []));
      if (meta?.geldigVanaf) setGeldigVanaf(meta.geldigVanaf);
      setLoading(false);
    }).catch((e) => { setFout(e.message); setLoading(false); });
  }, [jaar, participantsUrl, heeftIndividueel]);

  // Filter deelnemers op actieve individuele series
  const gefilterdOpSeries = useMemo(() => {
    const individueleSeries = actieveSeries.filter((s) => s !== 'onk');
    const individueelRijen = individueleSeries.length
      ? deelnemers.filter((p) => individueleSeries.some((s) => SERIE_CONFIG[s]?.filter?.(p)))
      : [];
    // ONK teams normaliseren en toevoegen als actief
    const onkRijen = heeftOnk ? onkTeams.map(normaliseerTeam) : [];
    return [...onkRijen, ...individueelRijen];
  }, [deelnemers, actieveSeries, onkTeams, heeftOnk]);

  // Oude uitslagen via eilanders.json — één fetch i.p.v. 14 per deelnemer
  const { zoekEdities, loading: eilandersLoading } = useEilandersLookup();

  // Bouw oldResults object { naam: [edities] } voor alle zichtbare deelnemers incl. ONK
  const oldResults = useMemo(() => {
    if (eilandersLoading) return {};
    const result = {};
    for (const p of gefilterdOpSeries) {
      if (p._type === 'team') {
        // ONK team — opzoeken per deelnemer
        const d1 = p._deelnemer1 || {};
        const d2 = p._deelnemer2 || {};
        if (d1.naam && !(d1.naam in result)) result[d1.naam] = zoekEdities({ Naam: d1.naam });
        if (d2.naam && !(d2.naam in result)) result[d2.naam] = zoekEdities({ Naam: d2.naam });
      } else {
        result[p.Naam] = zoekEdities(p);
      }
    }
    return result;
  }, [gefilterdOpSeries, zoekEdities, eilandersLoading]);

  const allFetched = !eilandersLoading;

  function toggleSerie(s) {
    setActieveSeries((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  }

  function handleExport() {
    downloadCsv(`speakerlijst-${jaar}.csv`, gefilterdOpSeries.map((p) => ({
      startnummer: p.StartNr,
      naam: p.Naam,
      woonplaats: p.Woonplaats || '',
      onderdeel: p.Onderdeel,
      uitslagen: (oldResults[p.Naam] || []).map((r) => `${r.year}: ${r.totaaltijd || r.tijdrit || '—'}${r.plaats && r.plaats !== '.' ? ` (${r.plaats}e)` : ''}`).join('; '),
    })));
  }

  function handleExportExcel() {
    exporteerExcel({
      jaar,
      deelnemers: gefilterdOpSeries,
      onkTeams,
      oldResults,
      geldigVanaf,
    });
  }

  function handleExportPdf() {
    exporteerPdf({
      jaar,
      deelnemers: gefilterdOpSeries,
      onkTeams,
      oldResults,
      geldigVanaf,
    });
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 1em', fontFamily: 'Arial, sans-serif', color: '#222' }}>
      {/* Series filter — alleen tonen bij 'all' */}
      {serie === 'all' && (
        <SerieCheckboxes
          zichtbareSeries={enkeleZichtbareSeries}
          actieveSeries={actieveSeries}
          onChange={toggleSerie}
          versie={import.meta.env.VITE_APP_VERSION}
        />
      )}

      {/* Gedeelde zoekbalk */}
      <ZoekBalk
        value={zoekterm}
        onChange={setZoekterm}
        placeholder="Zoek op naam, teamnaam of startnummer..."
        speaker={speaker}
      />

      {/* Gecombineerde lijst: individuele deelnemers + ONK teams */}
      <DeelnemersWeergave
        deelnemers={gefilterdOpSeries}
        geldigVanaf={geldigVanaf}
        loading={loading || (heeftOnk && onkLoading)}
        fout={fout || onkFout}
        speaker={speaker}
        oldResults={oldResults}
        allFetched={allFetched}
        onExport={handleExport}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        zoekterm={zoekterm}
        actieveSeries={actieveSeries}
        notes={notes}
      />

      {actieveSeries.length === 0 && (
        <p style={{ color: '#888' }}>Selecteer minimaal één serie hierboven.</p>
      )}
    </div>
  );
}
